import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'motion/react'
import { Flame, Snowflake } from 'lucide-react'
import confetti from 'canvas-confetti'
import { nextStreakMilestone } from '@tfa/shared'
import { TuCoinIcon } from '../TuCoinIcon'
import { api } from '../../services/api'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  /** Текущая длина серии (включая только что закрытый день). */
  streakDays: number
  /** TuCoin, фактически начисленные за milestone серии этим отчётом (с сервера). */
  bonusTucoin?: number
}

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

// Мотивационные хвосты под заголовком «N дней в режиме!» — случайный при показе.
const PHRASES = [
  'Ты молодец!',
  'Продолжай в том же духе!',
  'Огонь не гаснет — так держать!',
  'Не сбавляй темп!',
  'Ещё один день в копилку!',
  'Красавчик, режим на месте! 🔥',
]

function daysWord(n: number): string {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return 'день'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'дня'
  return 'дней'
}

// #339: следующая веха — через nextStreakMilestone из @tfa/shared с учётом уже
// полученных бонусов (STREAK_DAY выдаётся один раз за жизнь клиента): после
// обрыва серии виджет не должен снова обещать «+2 за 7д», если 7д уже выдана.

/** Анимированный счётчик дня режима: 0 → target с ease-out за ~0.9с. */
function AnimatedDays({ target }: { target: number }) {
  const [value, setValue] = useState(0)
  const rafRef = useRef<number | null>(null)
  useEffect(() => {
    const start = performance.now()
    const DURATION = 900
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / DURATION)
      const eased = 1 - Math.pow(1 - p, 3) // ease-out cubic
      setValue(Math.max(1, Math.round(eased * target)))
      if (p < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [target])
  // Размер шрифта подбираем под количество цифр, чтобы 365 влезло в круг.
  const fontSize = target >= 100 ? 34 : 44
  return (
    <span
      data-testid="streak-day-counter"
      style={{
        fontSize, fontWeight: 900, color: 'white', lineHeight: 1,
        fontVariantNumeric: 'tabular-nums', fontFamily: F,
        textShadow: '0 2px 8px rgba(0,0,0,0.18)',
      }}
    >
      {value}
    </span>
  )
}

interface Cell { day: number; inStreak: boolean; frozen: boolean; isToday: boolean; isFuture: boolean; empty?: boolean }

/** Локальная дата YYYY-MM-DD (как ключи в /streak/active-days). */
function fmtDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Календарь строится из РЕАЛЬНОЙ истории (activeDays + frozenDays из API), как и
// шторка истории «В режиме» — чтобы оба окна показывали одни и те же дни.
// Раньше использовалось `diff < streakDays`, из-за чего поп-ап рисовал только
// последнюю непрерывную серию и расходился со шторкой при заморозках (bug #219).
function buildMonth(now: Date, activeDays: Set<string>, frozenDays: Set<string>): Cell[] {
  const today = new Date(now); today.setHours(0, 0, 0, 0)
  const year = today.getFullYear(), month = today.getMonth()
  const first = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const firstDow = (first.getDay() + 6) % 7 // Mon-first
  const cells: Cell[] = []
  for (let i = 0; i < firstDow; i++) cells.push({ day: 0, inStreak: false, frozen: false, isToday: false, isFuture: false, empty: true })
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d); date.setHours(0, 0, 0, 0)
    const diff = Math.round((today.getTime() - date.getTime()) / 86400000)
    const isFuture = diff < 0
    const isToday = diff === 0
    const iso = fmtDay(date)
    const inStreak = !isFuture && activeDays.has(iso)
    const frozen = !isFuture && !inStreak && frozenDays.has(iso)
    cells.push({ day: d, inStreak, frozen, isToday, isFuture })
  }
  return cells
}

export function StreakSuccessModal({ open, onClose, streakDays, bonusTucoin = 0 }: Props) {
  const now = useMemo(() => new Date(), [])
  // Реальная история дней — тот же источник, что и у шторки «В режиме».
  // staleTime 0: только что закрытый день должен сразу попасть в календарь.
  const { data: activeData } = useQuery({
    queryKey: ['streak', 'active-days'],
    queryFn: () => api.getActiveDays(),
    enabled: open,
    staleTime: 0,
  })
  const activeDays = useMemo(() => new Set(activeData?.days ?? []), [activeData])
  // #532: дни паузы программы — та же снежинка, что и личная заморозка (иначе
  // поп-ап разошёлся бы со шторкой истории, где они уже мостят серию).
  const frozenDays = useMemo(
    () => new Set([...(activeData?.frozenDays ?? []), ...(activeData?.pausedDays ?? [])]),
    [activeData],
  )
  const cells = useMemo(() => buildMonth(now, activeDays, frozenDays), [now, activeDays, frozenDays])
  const next = useMemo(
    () => nextStreakMilestone(streakDays, activeData?.claimedMilestones ?? []),
    [streakDays, activeData],
  )
  // Фраза фиксируется на время показа (меняется при каждом новом открытии).
  const [phrase, setPhrase] = useState('')
  useEffect(() => {
    if (!open) return
    setPhrase(PHRASES[Math.floor(Math.random() * PHRASES.length)])
    const colors = ['#FF3B30', '#FF9500', '#FFD60A', '#FF6B00']
    const end = Date.now() + 1400
    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors })
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors })
      if (Date.now() < end) requestAnimationFrame(frame)
    }
    frame()
  }, [open, streakDays])

  const word = daysWord(streakDays)
  const wordTitle = word.charAt(0).toUpperCase() + word.slice(1)

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(10,18,40,0.62)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, fontFamily: F,
          }}
        >
          <motion.div
            data-testid="modal-streak-success"
            initial={{ scale: 0.82, opacity: 0, y: 24 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.88, opacity: 0, y: 12 }}
            transition={{ type: 'spring', damping: 20, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 340,
              borderRadius: 28, padding: '28px 22px 24px',
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
              boxShadow: '0 28px 72px rgba(255,107,0,0.30), 0 0 0 1px rgba(255,255,255,0.6)',
              textAlign: 'center', position: 'relative',
            }}
          >
            {/* Hero: анимированная цифра дня режима, огонёк — бейджем в углу */}
            <motion.div
              initial={{ scale: 0.4, rotate: -12 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 14 }}
              style={{
                width: 96, height: 96, borderRadius: '50%', margin: '0 auto 4px',
                background: streakDays >= 21
                  ? 'linear-gradient(135deg,#FF3B30,#C70021)'
                  : streakDays >= 7
                    ? 'linear-gradient(135deg,#FF9500,#FF6B00)'
                    : 'linear-gradient(135deg,#FFD60A,#FF9500)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 12px 32px rgba(255,107,0,0.4)',
                position: 'relative',
              }}
            >
              <AnimatedDays target={streakDays} />
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
                style={{
                  position: 'absolute', right: -6, bottom: -2,
                  width: 34, height: 34, borderRadius: '50%',
                  background: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(255,107,0,0.35)',
                }}
              >
                <Flame size={20} color="#FF6B00" fill="#FFB02E" />
              </motion.div>
            </motion.div>

            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1C2D41', margin: '12px 0 4px', lineHeight: 1.2 }}>
              {wordTitle} в режиме!
            </h1>
            <p style={{ fontSize: 14, color: '#6B7B8D', margin: '0 0 14px', lineHeight: 1.45 }}>
              {phrase}
            </p>

            {/* TuCoin: начисленный milestone-бонус и/или когда следующий.
                Монетка — ИНЛАЙН в тексте рядом с «+N», чтобы не отрывалась при
                переносе строки (баг: при flex-раскладке иконка зависала слева). */}
            {(bonusTucoin > 0 || next) && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                {bonusTucoin > 0 && (
                  <motion.div
                    data-testid="streak-bonus-tucoin"
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.5, type: 'spring', stiffness: 360, damping: 14 }}
                    style={{
                      alignSelf: 'center', padding: '8px 16px', borderRadius: 14,
                      background: 'linear-gradient(135deg,#FFF4D6,#FFE3A3)',
                      border: '1px solid rgba(224,131,0,0.35)',
                      boxShadow: '0 6px 16px rgba(255,149,0,0.25)',
                      fontSize: 15, fontWeight: 800, color: '#8A5800',
                      textAlign: 'center', lineHeight: 1.4,
                    }}
                  >
                    +{bonusTucoin}
                    <TuCoinIcon size={20} style={{ verticalAlign: '-5px', margin: '0 3px' }} />
                    за {streakDays} {word} подряд!
                  </motion.div>
                )}
                {next && (
                  <div
                    data-testid="streak-next-bonus"
                    style={{ fontSize: 12.5, color: '#6B7B8D', fontWeight: 600, textAlign: 'center', lineHeight: 1.5, padding: '0 8px' }}
                  >
                    +{next.reward}
                    <TuCoinIcon size={13} glow={false} style={{ verticalAlign: '-2px', margin: '0 2px' }} />
                    — ещё через {next.day - streakDays} {daysWord(next.day - streakDays)} режима
                  </div>
                )}
              </div>
            )}

            {/* Mini calendar with flames; today's cell pops in */}
            <div style={{ background: '#FBF7F2', borderRadius: 16, padding: '10px 10px 12px', marginBottom: 20, border: '1px solid rgba(255,149,0,0.15)' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#B45309', marginBottom: 8 }}>
                {MONTH_NAMES[now.getMonth()]} {now.getFullYear()}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 4 }}>
                {WEEKDAYS.map((w, i) => (
                  <div key={w} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: i >= 5 ? '#FF6B00' : '#9AA5B4' }}>{w}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                {cells.map((c, i) => {
                  if (c.empty) return <div key={i} style={{ aspectRatio: '1 / 1' }} />
                  // Сегодня только что закрыт этим отчётом — горит сразу, даже
                  // пока запрос active-days ещё не вернулся (без пустого мелькания).
                  const lit = c.inStreak || (c.isToday && streakDays > 0)
                  const cell = (
                    <div
                      style={{
                        aspectRatio: '1 / 1', borderRadius: 8,
                        background: c.isToday
                          ? 'linear-gradient(135deg,#FF9500,#FF6B00)'
                          : lit ? 'rgba(255,179,82,0.5)' : c.frozen ? 'rgba(74,144,226,0.14)' : 'rgba(26,58,92,0.04)',
                        border: c.isToday ? '2px solid #C70021' : 'none',
                        boxShadow: c.isToday ? '0 4px 12px rgba(255,107,0,0.45)' : c.frozen ? '0 0 0 1px rgba(74,144,226,0.30)' : 'none',
                        opacity: c.isFuture ? 0.3 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      <span style={{ position: 'absolute', top: 2, left: 3, fontSize: 8, fontWeight: 700, color: c.isToday ? 'rgba(255,255,255,0.9)' : lit ? '#6B5C40' : c.frozen ? '#3B7CC4' : '#9AA5B4' }}>{c.day}</span>
                      {lit && <Flame size={c.isToday ? 16 : 12} color={c.isToday ? 'white' : '#FF7A00'} fill={c.isToday ? '#FFD60A' : '#FFC180'} />}
                      {c.frozen && !lit && <Snowflake size={11} color="#4A90E2" />}
                    </div>
                  )
                  // Сегодняшний день — анимированный «пых» (только что закрыт).
                  return c.isToday ? (
                    <motion.div key={i} initial={{ scale: 0.2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.25, type: 'spring', stiffness: 420, damping: 12 }}>
                      {cell}
                    </motion.div>
                  ) : (
                    <div key={i}>{cell}</div>
                  )
                })}
              </div>
            </div>

            <button
              data-testid="btn-close-streak-success"
              onClick={onClose}
              style={{
                display: 'block', width: '100%', height: 52, borderRadius: 16, border: 'none',
                background: 'linear-gradient(135deg,#FF9500,#FF6B00)',
                color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: F,
                boxShadow: '0 8px 24px rgba(255,107,0,0.32)',
              }}
            >
              Продолжить
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
